import { CheckCircle2, Circle } from 'lucide-react'
import type { ValidationAnalysisResponse } from '@/lib/api/client'

type ApiStageScore = ValidationAnalysisResponse['stageScores'][number]
type ApiSignal = ApiStageScore['signals'][number]
type ApiQuote = ApiStageScore['evidenceQuotes'][number]
type ApiRec = ApiStageScore['recommendations'][number]

interface ValidationStageDetailProps {
  question: string
  stageScore?: ApiStageScore
  signalsFromCatalog?: { key: string; name: string; description: string }[]
}

export function ValidationStageDetail({
  question,
  stageScore,
  signalsFromCatalog = [],
}: ValidationStageDetailProps) {
  const signals = stageScore?.signals ?? []
  const quotes = stageScore?.evidenceQuotes ?? []
  const recs = stageScore?.recommendations ?? []

  return (
    <div
      style={{
        marginTop: 14,
        paddingTop: 14,
        borderTop: '1px solid var(--color-border)',
      }}
    >
      <p
        className="m-0 text-fg-secondary"
        style={{
          fontSize: 13.5,
          lineHeight: 1.55,
          maxWidth: '70ch',
          marginBottom: 18,
        }}
      >
        <span className="text-fg-muted">— </span>
        {question}
      </p>

      <div
        className="grid"
        style={{
          gridTemplateColumns:
            'minmax(180px, 0.75fr) minmax(0, 1.7fr) minmax(0, 1.05fr)',
        }}
      >
        <Column label={`Signals · ${signals.filter((s) => s.isValidated).length}/${signals.length || signalsFromCatalog.length}`}>
          <SignalList signals={signals} catalog={signalsFromCatalog} />
        </Column>

        <Column label={`Evidence · ${quotes.length}`}>
          {quotes.length === 0 ? (
            <Empty>No quotes yet.</Empty>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns:
                  quotes.length > 2 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
                columnGap: 22,
                rowGap: 14,
              }}
            >
              {quotes.map((quote, i) => (
                <Quote key={i} quote={quote} />
              ))}
            </div>
          )}
        </Column>

        <Column label={`Next steps · ${recs.length}`} isLast>
          {recs.length === 0 ? (
            <Empty>No recommendations.</Empty>
          ) : (
            <ul
              style={{
                margin: 0,
                padding: 0,
                listStyle: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              {recs.map((rec, i) => (
                <RecItem key={i} rec={rec} />
              ))}
            </ul>
          )}
        </Column>
      </div>

      {stageScore && (
        <div
          className="text-fg-muted font-mono"
          style={{
            marginTop: 16,
            paddingTop: 12,
            borderTop: '1px dashed var(--color-border)',
            fontSize: 11,
            letterSpacing: '0.02em',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 5,
              height: 5,
              borderRadius: 999,
              background: stageScore.confidenceScore >= 60 ? 'var(--color-success)' : stageScore.confidenceScore >= 40 ? 'var(--color-warning)' : 'var(--color-danger)',
            }}
          />
          <span>{stageScore.confidenceScore}% confidence</span>
          <span style={{ opacity: 0.4 }}>·</span>
          <span>{signals.filter((s) => s.isValidated).length} of {signals.length || signalsFromCatalog.length} signals validated</span>
          <span style={{ opacity: 0.4 }}>·</span>
          <span>Status: {stageScore.status.replace('_', ' ')}</span>
        </div>
      )}
    </div>
  )
}

function Column({
  label,
  isLast,
  children,
}: {
  label: string
  isLast?: boolean
  children: React.ReactNode
}) {
  return (
    <div
      style={{
        padding: '0 18px',
        borderRight: isLast ? 'none' : '1px solid var(--color-border)',
      }}
    >
      <div
        className="text-fg-muted font-mono uppercase"
        style={{
          fontSize: 10,
          letterSpacing: '0.1em',
          marginBottom: 12,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <p className="m-0 text-fg-muted italic" style={{ fontSize: 12.5 }}>
      {children}
    </p>
  )
}

function SignalList({
  signals,
  catalog,
}: {
  signals: ApiSignal[]
  catalog: { key: string; name: string; description: string }[]
}) {
  const items =
    signals.length > 0
      ? signals.map((s) => ({
          key: s.signalKey,
          name: s.signalName,
          isValidated: s.isValidated,
        }))
      : catalog.map((c) => ({
          key: c.key,
          name: c.name,
          isValidated: false,
        }))

  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((s, i) => (
        <li
          key={s.key || i}
          style={{
            display: 'grid',
            gridTemplateColumns: '14px 1fr',
            gap: 8,
            alignItems: 'start',
            fontSize: 13,
            lineHeight: 1.45,
            color: 'var(--color-fg)',
          }}
        >
          <span
            style={{
              color: s.isValidated ? 'var(--color-success-fg)' : 'var(--color-border-strong)',
              marginTop: 2,
            }}
          >
            {s.isValidated ? <CheckCircle2 size={13} /> : <Circle size={13} />}
          </span>
          <span className={s.isValidated ? '' : 'text-fg-muted'}>{s.name}</span>
        </li>
      ))}
    </ul>
  )
}

function Quote({ quote }: { quote: ApiQuote }) {
  return (
    <div style={{ breakInside: 'avoid' }}>
      <p
        className="m-0 text-fg italic"
        style={{
          fontSize: 12.5,
          lineHeight: 1.5,
        }}
      >
        “{quote.quote}”
      </p>
      <div
        className="text-fg-muted font-mono"
        style={{ fontSize: 11, marginTop: 4, letterSpacing: '0.02em' }}
      >
        — {quote.sourceTitle}
      </div>
    </div>
  )
}

function RecItem({ rec }: { rec: ApiRec }) {
  const dotColor =
    rec.priority === 'high'
      ? 'var(--color-danger)'
      : rec.priority === 'medium'
        ? 'var(--color-warning)'
        : 'var(--color-fg-muted)'
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: '6px 1fr',
        gap: 10,
        alignItems: 'start',
        fontSize: 13,
        lineHeight: 1.5,
        color: 'var(--color-fg)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          background: dotColor,
          marginTop: 6,
        }}
      />
      <div>
        <div style={{ fontWeight: 500, marginBottom: 1 }}>{rec.title}</div>
        <div className="text-fg-muted" style={{ fontSize: 12.5, lineHeight: 1.45 }}>
          {rec.description}
        </div>
      </div>
    </li>
  )
}
